import React from "react";
import {
  ClockConfig,
  HeaderWorldClock,
  WelcomeTitle
} from '@backstage/plugin-home';
import { Content, Header, InfoCard, Page } from '@backstage/core-components';
import { Link, makeStyles, Typography } from "@material-ui/core";
import logo from '../../static/maf-retail-platform.png'

const clockConfigs: ClockConfig[] = [
  {
    label: 'UAE',
    timeZone: 'Asia/Dubai',
  },
  {
    label: 'IND',
    timeZone: 'Asia/Kolkata',
  },
  {
    label: 'UTC',
    timeZone: 'UTC',
  }
];

const timeFormat: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
};

const useStyles = makeStyles({
  info: {
    width: 'auto',
    height: 'auto',
  },
  path: {
    fill: '#7df3e1',
  },
  image: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '2em',
    flexWrap: 'wrap',
    width: '80%',
    maxWidth: 1440,
    marginLeft: 'auto',
    marginRight: 'auto'
  },
});

export const HomePage = () => {
  const styles = useStyles()
  return (
    <Page themeId="home">
      <Header title={<WelcomeTitle language={['English']} />} pageTitleOverride="Home">
        <HeaderWorldClock
          clockConfigs={clockConfigs}
          customTimeFormat={timeFormat}
        />
      </Header>
      <Content>
        <InfoCard className={styles.info} title="Introduction to MAF Retail Platform" variant='flex' >
          <Typography variant="body1" gutterBottom>
            The <strong>MAF Retail Platform</strong> is a comprehensive web portal designed to power and transform the digital retail ecosystem. It provides an array of powerful <strong>Retail APIs</strong> and SaaS-based solutions tailored to meet the needs of modern commerce. With seamless integration capabilities, MAF Retail Platform enables retailers, suppliers, and partners to deliver superior customer experiences across various channels and geographies.
          </Typography>
          <img className={styles.image} src={logo} />



          <Typography variant="h5" gutterBottom>
            Core Retail APIs
          </Typography>

          <Typography variant="body1" gutterBottom>
            The platform offers a suite of <strong>Retail APIs</strong> that address key areas of retail operations, driving efficiency and scalability for e-commerce and omnichannel retail:
          </Typography>

          <Typography variant="body1" component="ul">
            <li>
              <strong><Link href="/catalog/default/component/Customer">Customer Service API</Link></strong>: Supports essential customer management functions, including Identity & Registration, Profile Management, Address Book, Saved Cards, Loyalty Program Integration, and Customer Communication (notifications and alerts).
            </li>
            <li>
              <strong><Link href="/catalog/default/component/Order">Order Service API</Link></strong>: Manages the entire order lifecycle, including Cart Management, Order Creation, Checkout, Returns, Refunds, and Order Management. This ensures a smooth, end-to-end customer purchase experience.
            </li>
            <li>
              <strong><Link href="/catalog/default/component/Payment">Payment API</Link></strong>: Enables payment processing across multiple channels, supporting card payments, cash on delivery, and other country-specific payment options such as Buy Now, Pay Later, facilitating a flexible checkout process.
            </li>
            <li>
              <strong><Link href="/catalog/default/component/Fullfilment">Fulfilment Service API</Link></strong>: Powers logistics and delivery operations, including Picking Service for order preparation, Last-Mile Delivery coordination, and Slot Service, offering precise delivery windows for online customers.
            </li>
            <li>
              <strong><Link href="/catalog/default/component/Product">Product Service API</Link></strong>: Provides robust product information management, including pricing, multi-variant pricing options across stores, regions, and channels. It also supports Promotion Services, enabling dynamic and targeted pricing strategies.
            </li>
            <li>
              <strong><Link href="/catalog/default/component/Search">Search and Recommendations API</Link></strong>: Utilizes Large Language Models (LLM) to deliver AI-driven search and personalized product recommendations, improving conversion rates through tailored suggestions based on customers' buying behavior.
            </li>
          </Typography>

          <Typography variant="h5" gutterBottom>
            SaaS-Based Solutions
          </Typography>

          <Typography variant="body1" gutterBottom>
            In addition to its Retail APIs, the MAF Retail Platform enables advanced SaaS-based products designed to enhance operational efficiency and boost sales performance:
          </Typography>

          <Typography variant="body1" component="ul">
            <li>
              <strong><Link href="https://uaepimhub.mafrservices.com/login/admin">PIM (Product Information Management)</Link></strong>: Centralizes and manages product data, streamlining the process of enriching and distributing accurate product information across various sales channels.
            </li>
            <li>
              <strong><Link href="https://supplier.carrefouruae.com/">Supplier Portal</Link></strong>: Empowers suppliers and third-party sellers to easily onboard, manage listings, track sales, and fulfill orders, expanding the retailer’s product offerings without increasing inventory.
            </li>
            <li>
              <strong><Link href="https://marketplace.carrefouruae.com/login/admin">Marketplace Portal</Link></strong>: A Marketplace Portal allows sellers to register, list products, and sell to customers. Sellers can either fulfill orders themselves or use Carrefour's Last Mile Solution for delivery, ensuring seamless transactions and efficient delivery.
            </li>
            <li>
              <strong><Link href="https://frameuat.azureedge.net/">CMS (Content Management Service)</Link></strong>: A flexible content management system that supports the creation, modification, and distribution of rich, engaging content across the platform, allowing for seamless content updates and personalization.
            </li>
          </Typography>

          <Typography variant="h5" gutterBottom>
            Business Benefits
          </Typography>

          <Typography variant="body1" gutterBottom>
            The MAF Retail Platform is built to drive digital transformation in retail by offering:
          </Typography>

          <Typography variant="body1" component="ul">
            <li>
              <strong>Enhanced Customer Experience</strong>: With intelligent search, personalized recommendations, and flexible payment and delivery options.
            </li>
            <li>
              <strong>Operational Efficiency</strong>: Through automated fulfilment, streamlined order management, and centralized product and content management.
            </li>
            <li>
              <strong>Scalability and Flexibility</strong>: Supporting multi-channel, multi-region, and diverse retail models, whether for direct-to-consumer or marketplace operations.
            </li>
          </Typography>

          <Typography variant="body1" gutterBottom>
            With the <strong>MAF Retail Platform</strong>, retailers can stay competitive, streamline their operations, and deliver exceptional customer experiences that scale effortlessly.
          </Typography>

        </InfoCard>
      </Content>
    </Page>
  )

}